import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "@/App.css";

import { AuthProvider } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { CartProvider } from "@/contexts/CartContext";
import { CurrencyProvider } from "@/contexts/CurrencyContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import CustomerRegister from "@/pages/CustomerRegister";
import Legal from "@/pages/Legal";
import ForgotPassword from "@/pages/ForgotPassword";

import CustomerHome from "@/pages/customer/Home";
import RestaurantDetail from "@/pages/customer/RestaurantDetail";
import Cart from "@/pages/customer/Cart";
import Checkout from "@/pages/customer/Checkout";
import Orders from "@/pages/customer/Orders";
import OrderTracking from "@/pages/customer/OrderTracking";
import Wallet from "@/pages/customer/Wallet";
import Profile from "@/pages/customer/Profile";
import Notifications from "@/pages/customer/Notifications";
import ServiceModulePage from "@/pages/customer/ServiceModulePage";

import RestaurantDashboard from "@/pages/restaurant/Dashboard";
import RestaurantMenu from "@/pages/restaurant/Menu";
import RestaurantOrders from "@/pages/restaurant/Orders";
import RestaurantSubscription from "@/pages/restaurant/Subscription";

import RiderDashboard from "@/pages/rider/Dashboard";
import RiderJobs from "@/pages/rider/Jobs";
import RiderEarnings from "@/pages/rider/Earnings";

import AdminDashboard from "@/pages/admin/Dashboard";
import ServiceModulesAdmin from "@/pages/admin/ServiceModules";
import AdminUsers from "@/pages/admin/Users";
import AdminRestaurants from "@/pages/admin/Restaurants";
import AdminSubscriptions from "@/pages/admin/Subscriptions";
import StrategyDocs from "@/pages/admin/StrategyDocs";
import AdminWaitlist from "@/pages/admin/Waitlist";
import AdminAuditLogs from "@/pages/admin/AuditLogs";
import AdminStates from "@/pages/admin/States";
import AdminCreateBusiness from "@/pages/admin/CreateBusiness";
import ProviderDetail from "@/pages/customer/ProviderDetail";
import ProviderDashboard from "@/pages/provider/Dashboard";

export default function App() {
  return (
    <ThemeProvider>
      <LanguageProvider>
        <AuthProvider>
          <CurrencyProvider>
            <CartProvider>
              <BrowserRouter>
              <Routes>
                <Route path="/" element={<Landing />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<CustomerRegister />} />
                <Route path="/business" element={<Register businessOnly />} />
                <Route path="/legal/:doc" element={<Legal />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />

                {/* Customer */}
                <Route path="/app" element={<ProtectedRoute roles={["customer"]}><CustomerHome /></ProtectedRoute>} />
                <Route path="/app/restaurant/:id" element={<ProtectedRoute roles={["customer"]}><RestaurantDetail /></ProtectedRoute>} />
                <Route path="/app/cart" element={<ProtectedRoute roles={["customer"]}><Cart /></ProtectedRoute>} />
                <Route path="/app/checkout" element={<ProtectedRoute roles={["customer"]}><Checkout /></ProtectedRoute>} />
                <Route path="/app/orders" element={<ProtectedRoute roles={["customer"]}><Orders /></ProtectedRoute>} />
                <Route path="/app/orders/:id" element={<ProtectedRoute roles={["customer"]}><OrderTracking /></ProtectedRoute>} />
                <Route path="/app/wallet" element={<ProtectedRoute roles={["customer"]}><Wallet /></ProtectedRoute>} />
                <Route path="/app/profile" element={<ProtectedRoute roles={["customer"]}><Profile /></ProtectedRoute>} />
                <Route path="/app/notifications" element={<ProtectedRoute roles={["customer"]}><Notifications /></ProtectedRoute>} />
                <Route path="/app/service/:key" element={<ProtectedRoute roles={["customer"]}><ServiceModulePage /></ProtectedRoute>} />
                <Route path="/app/service/:key/provider/:pid" element={<ProtectedRoute roles={["customer"]}><ProviderDetail /></ProtectedRoute>} />

                {/* Restaurant */}
                <Route path="/restaurant" element={<ProtectedRoute roles={["restaurant_owner", "admin"]}><RestaurantDashboard /></ProtectedRoute>} />
                <Route path="/restaurant/menu" element={<ProtectedRoute roles={["restaurant_owner", "admin"]}><RestaurantMenu /></ProtectedRoute>} />
                <Route path="/restaurant/orders" element={<ProtectedRoute roles={["restaurant_owner", "admin"]}><RestaurantOrders /></ProtectedRoute>} />
                <Route path="/restaurant/subscription" element={<ProtectedRoute roles={["restaurant_owner", "admin"]}><RestaurantSubscription /></ProtectedRoute>} />

                {/* Rider */}
                <Route path="/rider" element={<ProtectedRoute roles={["rider", "admin"]}><RiderDashboard /></ProtectedRoute>} />
                <Route path="/rider/jobs" element={<ProtectedRoute roles={["rider", "admin"]}><RiderJobs /></ProtectedRoute>} />
                <Route path="/rider/earnings" element={<ProtectedRoute roles={["rider", "admin"]}><RiderEarnings /></ProtectedRoute>} />

                {/* Provider (multi-module merchant) */}
                <Route path="/provider" element={<ProtectedRoute roles={["provider", "admin"]}><ProviderDashboard /></ProtectedRoute>} />

                {/* Admin */}
                <Route path="/admin" element={<ProtectedRoute roles={["admin"]}><AdminDashboard /></ProtectedRoute>} />
                <Route path="/admin/service-modules" element={<ProtectedRoute roles={["admin"]}><ServiceModulesAdmin /></ProtectedRoute>} />
                <Route path="/admin/users" element={<ProtectedRoute roles={["admin"]}><AdminUsers /></ProtectedRoute>} />
                <Route path="/admin/restaurants" element={<ProtectedRoute roles={["admin"]}><AdminRestaurants /></ProtectedRoute>} />
                <Route path="/admin/subscriptions" element={<ProtectedRoute roles={["admin"]}><AdminSubscriptions /></ProtectedRoute>} />
                <Route path="/admin/strategy" element={<ProtectedRoute roles={["admin"]}><StrategyDocs /></ProtectedRoute>} />
                <Route path="/admin/waitlist" element={<ProtectedRoute roles={["admin"]}><AdminWaitlist /></ProtectedRoute>} />
                <Route path="/admin/audit-logs" element={<ProtectedRoute roles={["admin"]}><AdminAuditLogs /></ProtectedRoute>} />
                <Route path="/admin/states" element={<ProtectedRoute roles={["admin"]}><AdminStates /></ProtectedRoute>} />
                <Route path="/admin/create-business" element={<ProtectedRoute roles={["admin"]}><AdminCreateBusiness /></ProtectedRoute>} />

                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </BrowserRouter>
            </CartProvider>
          </CurrencyProvider>
        </AuthProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}
